import { Modal } from "@msuite/ui/modal";
import { DataTable, getFlatRowSelection } from "@msuite/ui/table";
import type { PersonioSyncItemSchema } from "@msuite/utils/schemas";
import { useState } from "react";
import { PERSONIO_SYNC_QUERY_KEY, usePersonioSync } from "@/hooks/use-personio-sync";
import { PersonioLinkMemberModal } from "@/modals/integrations/personio-link-member-modal";
import { PersonioSyncContext } from "./context";
import { PersonioSyncSummary } from "./personio-sync-summary";
import { columns } from "./table-config";
import {
	getTakenUserIds,
	type PersonioSyncOverride,
	type PersonioSyncOverrides,
	type PersonioSyncRow,
	type PersonioSyncRun,
	toPersonioSyncItems,
	toPersonioSyncRows,
} from "./utils";

/** Props Interface */
interface PersonioSyncProps {
	run: PersonioSyncRun | null;
	onSelectionChange: (items: PersonioSyncItemSchema[]) => void;
}

export function PersonioSync({ run, onSelectionChange }: PersonioSyncProps) {
	/** State */
	const [overrides, setOverrides] = useState<PersonioSyncOverrides>({});
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [linkingRow, setLinkingRow] = useState<PersonioSyncRow | null>(null);

	/** Query */
	const { people, createBlocker, isLoading, refetch } = usePersonioSync();

	/** Functions */
	function handleOnRowSelectionChange(state: Parameters<typeof getFlatRowSelection>[0]) {
		const ids = getFlatRowSelection(state);
		setSelectedIds(ids);
		onSelectionChange(toPersonioSyncItems(rows, ids));
	}

	function handleOnOverride(personId: string, override: PersonioSyncOverride | null) {
		const next = { ...overrides };
		if (override) next[personId] = override;
		else delete next[personId];
		setOverrides(next);
		onSelectionChange(
			toPersonioSyncItems(toPersonioSyncRows(people, next, run?.results ?? null), selectedIds),
		);
	}

	function handleOnLinkMember(userId: string, userName: string) {
		if (!linkingRow) return;
		handleOnOverride(linkingRow.id, { action: "link", userId, userName });
	}

	/** Computed */
	const rows = toPersonioSyncRows(people, overrides, run?.results ?? null);

	/** Render */
	return (
		<PersonioSyncContext.Provider
			value={{
				onRequestLink: setLinkingRow,
				onCreateAccount: (row) => handleOnOverride(row.id, { action: "create" }),
				onReset: (row) => handleOnOverride(row.id, null),
			}}
		>
			<Modal.TableWrapper>
				<DataTable
					data={rows}
					columns={columns}
					isLoading={isLoading}
					refetch={refetch}
					queryKey={PERSONIO_SYNC_QUERY_KEY}
					menu={null}
					persistenceKey="personio-sync-table"
					enableRowSelection={(row) => row.original.action !== null}
					enableMultiRowSelection
					onRowSelectionChange={handleOnRowSelectionChange}
					slots={{
						"above-search": () => (
							<PersonioSyncSummary
								createBlocker={createBlocker}
								run={run}
							/>
						),
					}}
				/>
			</Modal.TableWrapper>
			{linkingRow && (
				<PersonioLinkMemberModal
					isOpen={!!linkingRow}
					onClose={() => setLinkingRow(null)}
					personName={`${linkingRow.firstName} ${linkingRow.lastName}`.trim()}
					excludeUserIds={getTakenUserIds(rows, linkingRow.id)}
					onSelect={handleOnLinkMember}
				/>
			)}
		</PersonioSyncContext.Provider>
	);
}
